import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { PageHeader } from '@/components/shared/PageHeader';
import { SeoAuditTab } from '@/components/webGrowth/SeoAuditTab';
import { Ga4OverviewPanel } from '@/components/webGrowth/Ga4OverviewPanel';
import { WebFunnelPanel } from '@/components/webGrowth/WebFunnelPanel';
import { WebBridgePanel } from '@/components/webGrowth/WebBridgePanel';
import { cn } from '@/lib/utils';

const GA4_DAY_WINDOWS = [14, 28, 90];

type WebTab = 'seo' | 'ga4' | 'funnels' | 'bridge';

const TABS: { id: WebTab; label: string }[] = [
    { id: 'seo', label: 'Search Console' },
    { id: 'ga4', label: 'GA4' },
    { id: 'funnels', label: 'Embudos' },
    { id: 'bridge', label: 'SEO ↔ GA4' },
];

function isWebTab(value: string | null): value is WebTab {
    return !!value && TABS.some((tab) => tab.id === value);
}

export function SeoAuditPage() {
    const [searchParams, setSearchParams] = useSearchParams();
    const tabFromUrl = searchParams.get('tab');
    const [activeTab, setActiveTab] = useState<WebTab>(() => (isWebTab(tabFromUrl) ? tabFromUrl : 'seo'));
    const [ga4Days, setGa4Days] = useState(28);

    useEffect(() => {
        if (isWebTab(tabFromUrl)) setActiveTab(tabFromUrl);
    }, [tabFromUrl]);

    function selectTab(tab: WebTab) {
        setActiveTab(tab);
        const next = new URLSearchParams(searchParams);
        next.set('tab', tab);
        setSearchParams(next, { replace: true });
    }

    return (
        <div className="space-y-6">
            <PageHeader
                title="Web"
                description="Search Console, GA4 landing y embudos versionados. Los crons nocturnos guardan series; esta pantalla solo las pinta."
            >
                {(activeTab === 'ga4' || activeTab === 'funnels' || activeTab === 'bridge') && (
                    <div className="flex gap-1 rounded-lg bg-muted/50 p-1">
                        {GA4_DAY_WINDOWS.map((value) => (
                            <button
                                key={value}
                                type="button"
                                onClick={() => setGa4Days(value)}
                                className={cn(
                                    'rounded-md px-3 py-1.5 text-sm font-medium',
                                    ga4Days === value ? 'bg-primary text-primary-foreground' : 'text-muted-foreground',
                                )}
                            >
                                {value} días
                            </button>
                        ))}
                    </div>
                )}
            </PageHeader>

            <div className="flex flex-wrap gap-1 rounded-lg bg-muted/50 p-1">
                {TABS.map((tab) => (
                    <button
                        key={tab.id}
                        type="button"
                        onClick={() => selectTab(tab.id)}
                        className={cn(
                            'rounded-md px-3 py-1.5 text-sm font-medium',
                            activeTab === tab.id ? 'bg-primary text-primary-foreground' : 'text-muted-foreground',
                        )}
                    >
                        {tab.label}
                    </button>
                ))}
            </div>

            {activeTab === 'seo' && <SeoAuditTab />}
            {activeTab === 'ga4' && <Ga4OverviewPanel days={ga4Days} />}
            {activeTab === 'funnels' && <WebFunnelPanel days={ga4Days} />}
            {activeTab === 'bridge' && <WebBridgePanel days={ga4Days} />}
        </div>
    );
}
